/**
 * Pure read-mode row shaping for the Vitals table, shared by
 * `<ObjectiveSection>` and the PDF export (`progressNotePdf.ts`) so the
 * exported table can't drift from the on-screen one.
 */
import type { ObservationItem } from '../progressNoteEditSchema'

// BP component LOINCs — systolic + diastolic fuse into one "Blood pressure" row in both read and edit modes; each observation still persists independently.
export const BP_SYSTOLIC_LOINC = '8480-6'
export const BP_DIASTOLIC_LOINC = '8462-4'

/** A row to render in READ mode — a real vital or the fused BP row. */
export type VitalReadRow = {
  key: string
  label: string
  /** Pre-formatted display (e.g. `148/92 mmHg` or `72 /min`). */
  value: string
  note?: string | null
  /** PD-1149 — server flagged this vital implausible; read views badge it. */
  needsReview?: boolean
}

// A UCUM annotation unit like `{score}` (pain 0–10) is a bare label, not a
// printable unit — drop it so a pain reading shows "7", not "7 {score}".
export const printableUnit = (unit: string | undefined): string =>
  unit && !unit.startsWith('{') ? unit : ''

/** Format a single observation's value + unit for read display. */
function formatVitalValue(vital: ObservationItem): string {
  if (vital.valueQuantity != null) {
    return `${vital.valueQuantity.value} ${printableUnit(vital.valueQuantity.unit)}`.trim()
  }
  return vital.valueString || '—'
}

/**
 * Read-mode "Blood pressure" value from the two component observations. Only
 * two numbers fuse as `sys/dia`; a component the doctor typed whole ("120/80")
 * shows as-is (never `120/80/80`), and a lone component shows alone. `null`
 * when neither has a value.
 */
function formatBloodPressure(
  systolic: ObservationItem | undefined,
  diastolic: ObservationItem | undefined,
): string | null {
  const sysNum = systolic?.valueQuantity?.value
  const diaNum = diastolic?.valueQuantity?.value
  const sysStr = systolic?.valueString?.trim() || null
  const diaStr = diastolic?.valueString?.trim() || null
  if (sysStr?.includes('/')) return `${sysStr} mmHg`
  if (diaStr?.includes('/')) return `${diaStr} mmHg`
  if (sysNum != null && diaNum != null) return `${sysNum}/${diaNum} mmHg`
  // A lone component renders in `sys/dia` shape with `—` for the missing side
  // (e.g. `—/92 mmHg`) so it can't be misread as a full reading.
  const sys = sysNum ?? sysStr
  const dia = diaNum ?? diaStr
  if (sys != null) return `${sys}/— mmHg`
  if (dia != null) return `—/${dia} mmHg`
  return null
}

/** Build READ-mode rows, fusing systolic + diastolic into one "Blood pressure" row at the first component's slot (one present → single value, no broken `148/`; neither → no row). */
export function buildVitalReadRows(vitals: ObservationItem[]): VitalReadRow[] {
  const systolic = vitals.find((v) => v.code === BP_SYSTOLIC_LOINC)
  const diastolic = vitals.find((v) => v.code === BP_DIASTOLIC_LOINC)
  const bpValue = formatBloodPressure(systolic, diastolic)
  let bpEmitted = false

  const rows: VitalReadRow[] = []
  for (const vital of vitals) {
    const isBp = vital.code === BP_SYSTOLIC_LOINC || vital.code === BP_DIASTOLIC_LOINC
    if (isBp) {
      if (bpValue != null && !bpEmitted) {
        rows.push({
          key: 'blood-pressure',
          label: 'Blood pressure',
          value: bpValue,
          note: systolic?.note || diastolic?.note || null,
          needsReview: Boolean(systolic?.needsReview || diastolic?.needsReview),
        })
        bpEmitted = true
      }
      continue
    }
    rows.push({
      key: vital.id,
      label: vital.display || vital.code || '—',
      value: formatVitalValue(vital),
      note: vital.note,
      needsReview: vital.needsReview,
    })
  }
  return rows
}
